import React, { useContext, useEffect, useMemo, useState } from "react";

import { Preview, TextState } from "@creatomate/preview";
import { classnames, MillisecondsField } from "@culturehq/components";
import { MillisecondsFieldValue } from "@culturehq/components/dist/components/form/MillisecondsField";
import { CreatomateContext } from "components/stories/video-studio/contexts/VideoStudioContext";
import { isSubtitleText, updateSubtitleSectionTimeById } from "../utils";


interface VttTimeProps {
  index: number;
  preview: Preview;
  subtitleElement: TextState;
}

const VttTime: React.FC<VttTimeProps> = ({ preview, subtitleElement, index }) => {
  const { elements } = preview.state || { elements: [] };
  const [isStartValid, setIsStartValid] = useState(true);
  const [isEndValid, setIsEndValid] = useState(true);

  const subtitleElements = useMemo(() => elements.filter(
    element => isSubtitleText(element.source.name)), [elements]
  );

  const {
    data: { subtitles },
    handleUpdateData,
    handleAutosaveDraft
  } = useContext(CreatomateContext);

  const startTime = Math.floor(subtitleElement.globalTime * 100) / 100;
  const endTime = Math.floor((subtitleElement.globalTime + subtitleElement.duration) * 100) / 100;

  const [startValue, setStartValue] = useState(startTime);
  const [endValue, setEndValue] = useState(endTime);

  const updateTime = (startTime: number, endTime: number) => {
    updateSubtitleSectionTimeById(
      subtitleElement.source.id,
      startTime,
      endTime - startTime,
      subtitles,
      handleAutosaveDraft
    );
  };

  const handleOnStartsAtChange = async (value: MillisecondsFieldValue) => {
    const newStartTime = typeof value === "number" ? value / 100 : 0;
    setStartValue(newStartTime);
    const newDuration = endTime - newStartTime;

    if (newDuration < 0.5) return;

    const previousElement = subtitleElements[index - 1];

    if (previousElement) {
      const { duration: prevDuration, globalTime: prevGlobalTime } = previousElement;
      const prevEndTime = prevGlobalTime + prevDuration;

      // element is overlapping with the previous subtitle element
      if (newStartTime < prevEndTime) {
        return;
      }
    }

    await preview.applyModifications({
      [`${subtitleElement.source.id}.time`]: newStartTime,
      [`${subtitleElement.source.id}.duration`]: newDuration
    });
    updateTime(newStartTime, endTime);
  };

  const handleOnEndsAtChange = async (value: MillisecondsFieldValue) => {
    if (typeof value !== "number") return;

    setEndValue(value / 100);

    const newEndTime = value / 100;
    const duration = newEndTime - startTime;
    const checkMin = (startTime + duration) >= startTime;
    if (!checkMin) return;

    const nextElement = subtitleElements[index + 1];

    if (nextElement) {
      const { globalTime: nextGlobalTime } = nextElement;

      // element is overlapping with the next subtitle element
      if (newEndTime > nextGlobalTime) {
        return;
      }
    }

    await preview.applyModifications({
      [`${subtitleElement.source.id}.duration`]: duration
    });
    updateTime(startTime, newEndTime);
  };

  const handleFocusSection = async () => {
    handleUpdateData({ currentSubtitleId: subtitleElement.source.id, updateSubtitles: false });
    preview.setActiveElements([subtitleElement.source.id]);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsStartValid(startTime === startValue);
    }, 300);

    return () => clearTimeout(timeout);
  }, [startTime, startValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsEndValid(endTime === endValue);
    }, 300);

    return () => clearTimeout(timeout);
  }, [endTime, endValue]);

  useEffect(() => {
    setStartValue(startTime);
    setEndValue(endTime);
  }, [endTime, startTime]);

  return (
    <div className="VTT-Container__Time">
      <div onBlur={() => { setStartValue(startTime); }}>
        <MillisecondsField
          min={0}
          className={classnames("chq-ffd-border", !isStartValid ? "chq-ffd-border__invalid" : "")}
          name={`start_at-${subtitleElement.source.id}`}
          id={`start_at-${subtitleElement.source.id}`}
          onChange={handleOnStartsAtChange}
          onFocus={handleFocusSection}
          value={startValue * 100}
        >
          <></>
        </MillisecondsField>
      </div>
      <div onBlur={() => { setEndValue(endTime); }}>
        <MillisecondsField
          min={0}
          className={classnames("chq-ffd-border", !isEndValid ? "chq-ffd-border__invalid" : "")}
          id={`ends_at-${subtitleElement.source.id}`}
          name={`ends_at-${subtitleElement.source.id}`}
          onChange={handleOnEndsAtChange}
          onFocus={handleFocusSection}
          value={endValue * 100}
        >
          <></>
        </MillisecondsField>
      </div>
    </div>
  );
};

export default VttTime;
